import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import ImageUpload from "../components/site/ImageUpload";
import { Trophy, Fish, Plus, X } from "lucide-react";

export default function Catches() {
  const { t, pick, lang } = useLang();
  const { user } = useAuth();
  const [catches, setCatches] = useState([]);
  const [board, setBoard] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    species_en: "", species_hu: "", weight_kg: "", length_cm: "",
    spot_name: "", notes: "", image_base64: null,
  });

  const load = async () => {
    const [c, b] = await Promise.all([api.get("/catches"), api.get("/catches/leaderboard")]);
    setCatches(c.data);
    setBoard(b.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.species_en || !form.weight_kg) {
      toast.error("Please fill in species and weight");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/catches", {
        species_en: form.species_en,
        species_hu: form.species_hu || form.species_en,
        weight_kg: parseFloat(form.weight_kg),
        length_cm: form.length_cm ? parseFloat(form.length_cm) : null,
        spot_name: form.spot_name,
        notes: form.notes,
        image_base64: form.image_base64,
      });
      toast.success(t("catches.success"));
      setShowForm(false);
      setForm({ species_en: "", species_hu: "", weight_kg: "", length_cm: "", spot_name: "", notes: "", image_base64: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-20 md:py-28 container-deg" data-testid="catches-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div className="max-w-2xl">
          <div className="overline">03 · {t("nav.catches")}</div>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 text-balance">{t("catches.heading")}</h1>
          <p className="text-ink-soft mt-6 leading-relaxed">{t("catches.sub")}</p>
        </div>
        {user ? (
          <button className="btn-primary" onClick={() => setShowForm(true)} data-testid="open-submit-catch">
            <Plus size={16} strokeWidth={1.5} className="mr-2" /> {t("catches.submit")}
          </button>
        ) : (
          <Link className="btn-ghost" to="/login" data-testid="catches-login-link">{t("catches.login_required")}</Link>
        )}
      </div>

      {/* Leaderboard */}
      <section className="mb-20" data-testid="leaderboard-section">
        <div className="flex items-center gap-3 mb-6">
          <Trophy size={20} strokeWidth={1.5} className="text-accent" />
          <h2 className="font-serif text-3xl">{t("catches.leaderboard")}</h2>
        </div>
        <div className="overflow-x-auto card-deg">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b border-line">
                <th className="px-6 py-4 overline">#</th>
                <th className="px-6 py-4 overline">{t("catches.by")} angler</th>
                <th className="px-6 py-4 overline text-right">{t("catches.total_weight")}</th>
                <th className="px-6 py-4 overline text-right">{t("catches.biggest")}</th>
                <th className="px-6 py-4 overline text-right">{t("catches.count")}</th>
              </tr>
            </thead>
            <tbody>
              {board.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-ink-soft">{t("catches.no_catches")}</td></tr>
              )}
              {board.map((b, i) => (
                <tr key={b.user_name} className="border-b border-line/60" data-testid={`leaderboard-row-${i}`}>
                  <td className="px-6 py-4 font-serif text-2xl text-primary">{i + 1}</td>
                  <td className="px-6 py-4 font-medium">{b.user_name}</td>
                  <td className="px-6 py-4 text-right font-serif text-xl">{b.total_weight.toFixed(2)} {t("common.kg")}</td>
                  <td className="px-6 py-4 text-right">{b.biggest_catch.toFixed(2)} {t("common.kg")}</td>
                  <td className="px-6 py-4 text-right">{b.catch_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Gallery */}
      <section data-testid="catch-gallery-section">
        <h2 className="font-serif text-3xl mb-6">{t("catches.gallery")}</h2>
        {catches.length === 0 ? (
          <p className="text-ink-soft py-16 text-center">{t("catches.no_catches")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {catches.map((c, i) => (
              <article key={c.id} className="card-deg overflow-hidden" data-testid={`catch-card-${i}`}>
                <div className="aspect-[4/3] bg-bg-muted">
                  {c.image_base64 ? (
                    <img src={c.image_base64} alt={pick(c, "species")} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-secondary">
                      <Fish size={56} strokeWidth={1} />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-2xl">{pick(c, "species")}</h3>
                    <span className="font-serif text-2xl text-accent">{c.weight_kg} <span className="text-xs text-ink-soft">{t("common.kg")}</span></span>
                  </div>
                  <div className="text-xs text-ink-soft mt-2 flex gap-3 flex-wrap">
                    {c.length_cm && <span>{c.length_cm} {t("common.cm")}</span>}
                    {c.spot_name && <span>· {c.spot_name}</span>}
                    <span>· {c.user_name}</span>
                  </div>
                  {c.notes && <p className="text-sm text-ink-soft mt-4 italic">"{c.notes}"</p>}
                  <div className="text-[11px] uppercase tracking-wider text-secondary mt-3">
                    {new Date(c.caught_at || c.created_at).toLocaleDateString(lang === "hu" ? "hu-HU" : "en-GB")}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Submit modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-primary/60 flex items-center justify-center p-4" data-testid="submit-catch-modal" onClick={() => setShowForm(false)}>
          <div className="bg-bg-paper max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-8 py-6 border-b border-line">
              <h2 className="font-serif text-3xl">{t("catches.submit")}</h2>
              <button onClick={() => setShowForm(false)} className="text-ink-soft hover:text-primary" data-testid="close-catch-modal"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="overline">{t("catches.species")} (EN)</label>
                  <input className="input-deg mt-2" value={form.species_en} onChange={(e) => setForm({ ...form, species_en: e.target.value })} required data-testid="catch-species-en" />
                </div>
                <div>
                  <label className="overline">{t("catches.species")} (HU)</label>
                  <input className="input-deg mt-2" value={form.species_hu} onChange={(e) => setForm({ ...form, species_hu: e.target.value })} data-testid="catch-species-hu" />
                </div>
                <div>
                  <label className="overline">{t("catches.weight")} ({t("common.kg")})</label>
                  <input type="number" step="0.01" className="input-deg mt-2" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} required data-testid="catch-weight" />
                </div>
                <div>
                  <label className="overline">{t("catches.length")} ({t("common.cm")})</label>
                  <input type="number" step="0.1" className="input-deg mt-2" value={form.length_cm} onChange={(e) => setForm({ ...form, length_cm: e.target.value })} data-testid="catch-length" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline">{t("catches.spot")}</label>
                  <input className="input-deg mt-2" value={form.spot_name} onChange={(e) => setForm({ ...form, spot_name: e.target.value })} data-testid="catch-spot" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline">{t("catches.notes")}</label>
                  <textarea rows={3} className="input-deg mt-2 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="catch-notes" />
                </div>
              </div>
              <div>
                <label className="overline mb-2 block">{t("catches.photo")}</label>
                <ImageUpload value={form.image_base64} onChange={(v) => setForm({ ...form, image_base64: v })} label={t("catches.photo")} testId="catch-image" />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60" data-testid="catch-submit-button">
                {submitting ? t("common.loading") : t("catches.submit_button")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
